import React, { useState } from 'react';
import { PencilIcon, SparklesIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components';
import { toast } from 'react-hot-toast';
import { useGeminiMutations } from '../../../../hooks/data/useProjectMutations';
import type { Project, ExpandDescriptionResponse } from '@/features/Projects';
import { AIOperationFeedback, AutoSaveIndicator } from '@/features/Projects/components/shared';
import { ProjectInsights } from '../shared/ProjectInsights';
import { useAIOperationState, useAutoSave } from '../../../../hooks';

interface InitialDescriptionPhaseProps {
  project: Project;
  onSave?: (description: string, insights?: ExpandDescriptionResponse) => Promise<void>;
  onNext?: () => void;
}

export default function InitialDescriptionPhase({ 
  project, 
  onSave, 
  onNext 
}: InitialDescriptionPhaseProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedDescription, setEditedDescription] = useState(project.description || '');
  const [lastInsights, setLastInsights] = useState<ExpandDescriptionResponse | null>(null);
  
  const { expandDescription } = useGeminiMutations();
  
  // AI Operation State for consistent feedback
  const aiOperationState = useAIOperationState({
    operation: "Regenerando Descrição com IA",
    canCancel: false,
    canRetry: true,
    canContinueInBackground: false,
    estimatedTime: 30 // 30 segundos estimado
  });

  // Auto-save state for description
  const autoSave = useAutoSave({
    data: editedDescription,
    onSave: async (description: string) => {
      if (description !== project.description && onSave) {
        await onSave(description, lastInsights || undefined);
      }
    },
    delay: 3000, // 3 segundos de delay
    enabled: isEditing && editedDescription.length > 10, // Só ativa se estiver editando e tiver conteúdo mínimo
    compareFunction: (prev, current) => prev !== current,
    showToast: false // Não mostrar toast para não interferir com o feedback visual
  });

  const handleSave = async () => {
    if (!editedDescription.trim()) {
      toast.error('Descrição não pode estar vazia');
      return;
    }

    if (editedDescription === project.description) {
      setIsEditing(false);
      autoSave.resetState();
      return;
    }

    try {
      await onSave?.(editedDescription, lastInsights || undefined);
      setIsEditing(false);
      autoSave.resetState(); // Reset auto-save state após salvamento manual
      toast.success('Descrição atualizada com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar descrição:', error);
      toast.error('Erro ao salvar descrição');
    }
  };

  const handleCancel = () => {
    setEditedDescription(project.description || '');
    setIsEditing(false);
    autoSave.resetState(); // Reset auto-save state quando cancelar
  };

  const handleRegenerateWithAI = async () => {
    if (!project.description?.trim()) {
      toast.error('É necessário ter uma descrição base para regenerar');
      return;
    }

    aiOperationState.startOperation();
    aiOperationState.setCurrentStep("Analisando descrição atual...");
    
    try {
      const response = await expandDescription({
        currentDescription: project.description,
        projectName: project.name,
        additionalContext: ''
      });

      if (response.expandedDescription) {
        setEditedDescription(response.expandedDescription);
        setLastInsights(response); // Armazenar todos os insights da resposta
        setIsEditing(true);
        aiOperationState.completeOperation();
        toast.success('Nova descrição gerada! Revise os insights antes de salvar.');
      }
    } catch (error) {
      console.error('Erro ao regenerar descrição:', error);
      aiOperationState.errorOperation(
        error instanceof Error ? error.message : 'Erro ao regenerar descrição com IA'
      );
    }
  };

  const handleRetryGeneration = () => {
    aiOperationState.resetOperation();
    handleRegenerateWithAI();
  };

  const isGeneratingDescription = ['preparing', 'processing', 'finalizing'].includes(aiOperationState.state.status);

  const canProceed = project.description && project.description.length > 20;

  return (
    <div className="space-y-6">
      {/* Feedback de operação de IA */}
      {(aiOperationState.state.status !== 'idle') && (
        <AIOperationFeedback
          operation={aiOperationState.operation}
          state={aiOperationState.state}
          onRetry={handleRetryGeneration}
          onDismiss={aiOperationState.resetOperation}
        />
      )}
      
      {/* Header da Fase */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Descrição do Projeto
          </h3>
          <p className="text-sm text-gray-600">
            Defina uma descrição clara e detalhada do seu projeto
          </p>
        </div>
        
        {/* Ações */}
        <div className="flex gap-2">
          {!isEditing ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                leftIcon={<PencilIcon className="w-4 h-4" />}
              >
                Editar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleRegenerateWithAI}
                disabled={isGeneratingDescription || !project.description}
                leftIcon={<SparklesIcon className="w-4 h-4" />}
                className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 text-purple-700 hover:from-purple-100 hover:to-indigo-100"
              >
                {isGeneratingDescription ? 'Regenerando...' : 'Regenerar com IA'}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCancel}
                leftIcon={<XMarkIcon className="w-4 h-4" />}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={handleSave}
                leftIcon={<CheckIcon className="w-4 h-4" />}
              >
                Salvar
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Conteúdo da Descrição */}
      <div className="bg-gray-50 rounded-lg p-6">
        {!isEditing ? (
          <div className="space-y-4">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900 mb-2">
                  Descrição Atual:
                </h4>
                {project.description ? (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {project.description}
                    </p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-yellow-800 text-sm">
                      Nenhuma descrição definida ainda. Clique em "Editar" para adicionar uma descrição.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Estatísticas */}
            {project.description && (
              <div className="flex gap-4 text-xs text-gray-500">
                <span>{project.description.length} caracteres</span>
                <span>≈ {Math.ceil(project.description.length / 5)} palavras</span>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Editar Descrição:
              </label>
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                placeholder="Digite uma descrição detalhada do seu projeto..."
                className="w-full h-48 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                autoFocus
              />
            </div>

            {/* Auto-save indicator */}
            {editedDescription.length > 10 && (
              <AutoSaveIndicator
                state={autoSave.state}
                onRetry={autoSave.retry}
                onManualSave={autoSave.triggerSave}
                compact
                className="mb-3"
              />
            )}

            {/* Estatísticas em tempo real */}
            <div className="flex justify-between items-center text-xs text-gray-500">
              <div className="flex gap-4">
                <span>{editedDescription.length} caracteres</span>
                <span>≈ {Math.ceil(editedDescription.length / 5)} palavras</span>
              </div>
              <div className={`font-medium ${editedDescription.length >= 50 ? 'text-green-600' : 'text-orange-600'}`}>
                {editedDescription.length >= 50 ? '✓ Descrição adequada' : 'Adicione mais detalhes'}
              </div>
            </div>

            {/* Dicas para uma boa descrição */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h5 className="text-sm font-medium text-blue-900 mb-2">
                💡 Dicas para uma boa descrição:
              </h5>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Explique qual problema o projeto resolve</li>
                <li>• Descreva o público-alvo ou usuários</li>
                <li>• Mencione as principais funcionalidades</li>
                <li>• Inclua o contexto de negócio se aplicável</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Project Insights */}
      {(project.descriptionInsights || lastInsights) && (
        <ProjectInsights 
          insights={{
            ...project.descriptionInsights,
            ...lastInsights
          }}
          type="description"
        />
      )}

      {/* Footer com ações de navegação */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        <div className="text-sm text-gray-600">
          {canProceed ? (
            <span className="text-green-600 flex items-center gap-1">
              <CheckIcon className="w-4 h-4" />
              Descrição completa - Pronto para próxima fase
            </span>
          ) : (
            <span className="text-orange-600">
              Adicione uma descrição mais detalhada (mín. 20 caracteres)
            </span>
          )}
        </div>
        
        {canProceed && onNext && (
          <Button onClick={onNext}>
            Próxima Fase →
          </Button>
        )}
      </div>
    </div>
  );
}